import { useEffect, useState } from 'react';
import { Dialog } from 'radix-ui';
import { MessageSquareIcon } from 'lucide-react';
import { formatCopy } from '@/i18n/ns';
import { SessionChangedError } from '@/lib/session-guard';
import {
  connectionGrantsApi,
  type ConnectionGrantAuthority,
  type ConnectionGrantKind,
  type ConnectionGrantState,
} from '../connection-grants/api';
import { useIntlLocale } from '../intl-locale';
import {
  Avatar,
  SheetButton,
  SheetSection,
  ShareSheet,
  ShareTrigger,
  useSheetCopy,
  type SheetCopy,
} from './share-sheet';

// What a shared item runs on, read-only for everyone (operator 2026-10-09).
// The connections belong to the item; nobody who opens it gets an account.
// Editors add, replace or remove one only in the item's chat, as a change
// they approve there. This view never writes.

export type RunsOnThing = 'theApp' | 'theAgent' | 'theSkill' | 'theResult' | 'theItem';
type Read =
  | { key: string; phase: 'loading' }
  | { key: string; phase: 'failed' }
  | { key: string; phase: 'ready'; state: ConnectionGrantState };

function useWorkConnections(kind: ConnectionGrantKind, id: string, session?: typeof fetch) {
  const key = `${kind}:${id}`;
  const [attempt, setAttempt] = useState(0);
  const [read, setRead] = useState<Read>({ key, phase: 'loading' });
  useEffect(() => {
    let current = true;
    setRead({ key, phase: 'loading' });
    connectionGrantsApi.get(kind, id, session).then(
      (state) => current && setRead({ key, phase: 'ready', state }),
      (error) => {
        if (current && !(error instanceof SessionChangedError)) setRead({ key, phase: 'failed' });
      },
    );
    return () => {
      current = false;
    };
  }, [key, kind, id, session, attempt]);
  return {
    read: read.key === key ? read : ({ key, phase: 'loading' } as const),
    retry: () => setAttempt((value) => value + 1),
  };
}

function listOf(values: string[], locale: string) {
  try {
    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(values);
  } catch {
    return values.join(', ');
  }
}

function meta(
  item: ConnectionGrantAuthority,
  selfId: string | null | undefined,
  copy: SheetCopy,
  locale: string,
) {
  const grantor = item.grant?.grantorUserId;
  const owner =
    grantor && grantor === selfId
      ? copy.yours
      : item.grant?.grantorName
        ? formatCopy(copy.from, { name: item.grant.grantorName })
        : copy.fromColleague;
  const labels = item.operations.map((operation) => operation.label);
  const reads = labels.length
    ? formatCopy(copy.reads, { list: listOf(labels, locale) })
    : copy.readsNothing;
  return `${owner} · ${reads}${item.grant?.allowChanges ? `, ${copy.canChange}` : ''}`;
}

function RunRow({
  item,
  selfId,
  thing,
  onFix,
}: {
  item: ConnectionGrantAuthority;
  selfId?: string | null;
  thing: string;
  onFix?: () => void;
}) {
  const copy = useSheetCopy();
  const locale = useIntlLocale();
  const name = item.name || copy.unnamedConnection;
  const tileId = item.provider || name;
  const grantor = item.grant?.grantorName;
  const codeChanged = item.pausedFor === 'code_changed';
  // The person who connected it, reading their own paused connection: they confirm it again.
  const yours = Boolean(selfId && item.grant?.grantorUserId === selfId);
  return (
    <div
      className={`ws-run${item.available ? '' : ' is-paused'}`}
      data-testid="share-run"
      data-available={item.available}
    >
      <div className="ws-run-main">
        <Avatar id={tileId} name={tileId} />
        <span className="ws-run-text">
          <span className="ws-run-title">
            <span className="ws-run-name">{name}</span>
            {!item.available && <span className="ws-run-pill">{copy.paused}</span>}
          </span>
          {item.available && (
            <span className="ws-run-meta">{meta(item, selfId, copy, locale)}</span>
          )}
        </span>
      </div>
      {!item.available && (
        <div className="ws-run-paused">
          <p>
            {codeChanged
              ? formatCopy(
                  yours
                    ? copy.pausedCodeChangedYou
                    : grantor
                      ? copy.pausedCodeChanged
                      : copy.pausedCodeChangedColleague,
                  { name: grantor ?? '', thing, connection: name },
                )
              : grantor
                ? formatCopy(copy.pausedBody, { name: grantor, thing, connection: name })
                : formatCopy(copy.pausedByColleague, { thing, connection: name })}
          </p>
          {onFix && (
            <div className="ws-run-fix">
              <SheetButton small onClick={onFix} data-testid="share-run-fix">
                <MessageSquareIcon aria-hidden="true" />
                {copy.fixInChat}
              </SheetButton>
              <span>{codeChanged && yours ? copy.fixInChatConfirmBody : copy.fixInChatBody}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The Runs on section. Viewers see the attached connections, or nothing when
 * there are none; editors also see where to change them: the item's chat.
 */
export function RunsOn({
  kind,
  id,
  thing,
  selfId,
  session,
  chatLabel,
  onChangeInChat,
}: {
  kind: ConnectionGrantKind;
  id: string;
  thing: RunsOnThing;
  selfId?: string | null;
  session?: typeof fetch;
  chatLabel?: string;
  /** Opens the item's chat. Absent where this person has no chat for it. */
  onChangeInChat?: () => void;
}) {
  const copy = useSheetCopy();
  const { read, retry } = useWorkConnections(kind, id, session);
  const named = copy[thing];
  if (read.phase === 'loading')
    return (
      <SheetSection title={copy.runsOn} testId="share-runs-on">
        <div className="ws-runs" aria-busy="true">
          <div className="ws-run is-skeleton" />
        </div>
      </SheetSection>
    );
  if (read.phase === 'failed')
    return (
      <SheetSection title={copy.runsOn} testId="share-runs-on">
        <p className="ws-sharesheet-help" role="alert">
          {copy.runsOnFailed}{' '}
          <SheetButton small onClick={retry}>
            {copy.retry}
          </SheetButton>
        </p>
      </SheetSection>
    );
  const items = read.state.items.filter((item) => item.enabled);
  const editor = read.state.canEdit;
  if (!items.length && !editor) return null;
  const fix = editor ? onChangeInChat : undefined;
  return (
    <SheetSection
      title={copy.runsOn}
      testId="share-runs-on"
      help={formatCopy(editor ? copy.runsOnHelp : copy.runsOnViewerHelp, { thing: named })}
    >
      {items.length ? (
        <div className="ws-runs">
          {items.map((item) => (
            <RunRow key={item.connectionId} item={item} selfId={selfId} thing={named} onFix={fix} />
          ))}
        </div>
      ) : (
        <p className="ws-sharesheet-help">{copy.runsOnEmpty}</p>
      )}
      {editor && (
        <div className="ws-sharesheet-chat-hint" data-testid="share-chat-hint">
          <span>{copy.chatHint}</span>
          {onChangeInChat && (
            <SheetButton small onClick={onChangeInChat} data-testid="share-change-in-chat">
              <MessageSquareIcon aria-hidden="true" />
              {chatLabel ?? copy.editInChat}
            </SheetButton>
          )}
        </div>
      )}
    </SheetSection>
  );
}

/**
 * Someone who can use shared work but not share it presses the same Share
 * button and sees, read-only, their access and what the work runs on.
 */
export function ViewerShareButton({
  kind,
  id,
  title,
  thing,
  ownerName,
  roleLabel,
  selfId,
  session,
  audienceLabel,
  testId = 'share-viewer',
}: {
  kind: ConnectionGrantKind;
  id: string;
  title: string;
  thing: RunsOnThing;
  ownerName?: string | null;
  roleLabel?: string;
  selfId?: string | null;
  session?: typeof fetch;
  /** Who can open it, when this person may know (the button's name). */
  audienceLabel?: string;
  testId?: string;
}) {
  const copy = useSheetCopy();
  const [open, setOpen] = useState(false);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {/* Without a known audience its name is its text, "Shared". */}
        <ShareTrigger viewer testId={testId} audienceLabel={audienceLabel} />
      </Dialog.Trigger>
      {open && (
        <ShareSheet
          eyebrow={copy.eyebrowShared}
          title={title}
          testId="share-viewer-sheet"
          actions={
            <Dialog.Close asChild>
              <SheetButton primary>{copy.done}</SheetButton>
            </Dialog.Close>
          }
        >
          <SheetSection title={copy.yourAccess}>
            <div className="ws-sharesheet-access">
              <span className="ws-person-text">
                <span className="ws-person-name">
                  {ownerName ? formatCopy(copy.sharedBy, { name: ownerName }) : copy.sharedWithYou}
                </span>
                {ownerName && (
                  <span className="ws-person-detail">
                    {formatCopy(copy.ownerDecides, { name: ownerName })}
                  </span>
                )}
              </span>
              <span className="ws-sharesheet-badge">{roleLabel ?? copy.canUse}</span>
            </div>
          </SheetSection>
          <RunsOn kind={kind} id={id} thing={thing} selfId={selfId} session={session} />
        </ShareSheet>
      )}
    </Dialog.Root>
  );
}
